import { gql } from '@apollo/client';

export const GET_PTO_USERS = gql`
  query GetPtoUsers($page: Int!, $limit: Int!, $search: String, $role: String) {
    paginatedUsers(page: $page, limit: $limit, search: $search, role: $role) {
      items {
        id
        firstName
        lastName
        name
        email
        role
        isActive
        paidTimeOffDays
      }
      total
      page
      limit
      hasMore
    }
  }
`;

/**
 * Query to get employee weekly schedule
 */
export const GET_EMPLOYEE_WEEKLY_SCHEDULE = gql`
  query GetEmployeeWeeklySchedule($userId: Int!) {
    employeeWeeklySchedule(userId: $userId) {
      dayOfWeek
      isWorking
      isRemote
    }
  }
`;

/**
 * Query to get schedules for multiple employees
 */
export const GET_EMPLOYEE_SCHEDULES = gql`
  query GetEmployeeSchedules($userIds: [Int!]) {
    employeeSchedules(userIds: $userIds) {
      userId
      employeeName
      weeklyPattern {
        dayOfWeek
        isWorking
        isRemote
      }
    }
  }
`;

/**
 * Query to get company holidays
 */
export const GET_COMPANY_HOLIDAYS = gql`
  query GetCompanyHolidays($startDate: Timestamp, $endDate: Timestamp, $year: Int) {
    companyHolidays(startDate: $startDate, endDate: $endDate, year: $year) {
      id
      name
      date
      description
      createdAt
      updatedAt
    }
  }
`;

/**
 * Query to get employee vacations
 */
export const GET_EMPLOYEE_VACATIONS = gql`
  query GetEmployeeVacations($userId: Int, $startDate: Timestamp, $endDate: Timestamp) {
    employeeVacations(userId: $userId, startDate: $startDate, endDate: $endDate) {
      id
      userId
      startDate
      endDate
      vacationType
      reason
      approvalStatus
      reviewedAt
      reviewNote
      reviewedBy {
        id
        name
      }
      createdAt
      updatedAt
      user {
        id
        firstName
        lastName
        name
      }
      createdBy {
        id
        firstName
        lastName
        name
      }
    }
  }
`;

/**
 * Admin: query an employee's currently designated coverage / back-up contact.
 */
export const GET_EMPLOYEE_COVERAGE = gql`
  query GetEmployeeCoverage($userId: Int!) {
    employeeCoverage(userId: $userId) {
      id
      firstName
      lastName
      name
      role
      email
      phoneNumber
      avatarUrl
    }
  }
`;

/**
 * Admin: query users eligible to be selected as coverage for a specific employee.
 */
export const GET_EMPLOYEE_COVERAGE_ELIGIBLE_USERS = gql`
  query GetEmployeeCoverageEligibleUsers($userId: Int!) {
    employeeCoverageEligibleUsers(userId: $userId) {
      id
      firstName
      lastName
      name
      role
      email
      phoneNumber
      avatarUrl
    }
  }
`;

/**
 * Check whether the given users are available on the given dates for
 * assignment. Only unavailable `(userId, date)` combinations are returned —
 * anything not in the response is available.
 */
export const CHECK_ASSIGNMENT_AVAILABILITY = gql`
  query CheckAssignmentAvailability($userIds: [Int!]!, $dates: [Timestamp!]!) {
    checkAssignmentAvailability(userIds: $userIds, dates: $dates) {
      userId
      userName
      date
      reason
      detail
      notes
    }
  }
`;

/**
 * Admin: query an employee's per-date schedule overrides within a range.
 */
export const GET_EMPLOYEE_SCHEDULE_OVERRIDES = gql`
  query GetEmployeeScheduleOverrides($userId: Int!, $startDate: Timestamp, $endDate: Timestamp) {
    employeeScheduleOverrides(userId: $userId, startDate: $startDate, endDate: $endDate) {
      id
      date
      isWorking
      isRemote
      notes
    }
  }
`;

/**
 * Query to get team availability tape chart data
 */
export const GET_TEAM_AVAILABILITY_TAPE_CHART = gql`
  query GetTeamAvailabilityTapeChart($startDate: Timestamp!, $endDate: Timestamp!, $filters: TeamAvailabilityFiltersInput) {
    teamAvailabilityTapeChart(startDate: $startDate, endDate: $endDate, filters: $filters) {
      items {
        employeeId
        employeeName
        employeeRole
        employeeAreas
        weeklyScheduleDaysSet
        hasCompleteWeeklySchedule
        paidTimeOffDays
        ptoDaysRemaining
        coverageUserId
        coverageUserName
        days {
          date
          status
          statusType
          holidayName
          vacationType
          isRemote
          isOverride
          notes
        }
      }
      total
      limit
      offset
      hasMore
    }
  }
`;

export const GET_TEAM_PTO_OVERVIEW = gql`
  query GetTeamPtoOverview($filters: TeamAvailabilityFiltersInput) {
    teamPtoOverview(filters: $filters) {
      items {
        employeeId
        employeeName
        employeeRole
        employeeAreas
        vacationId
        vacationType
        approvalStatus
        startDate
        endDate
        isCurrent
        daysUntilStart
        daysRemaining
        ptoDaysRemaining
        paidTimeOffDays
      }
      total
      limit
      offset
      hasMore
    }
  }
`;

export const GET_TEAM_MEMBER_PTO_HISTORY = gql`
  query GetTeamMemberPtoHistory($userId: Int!) {
    teamMemberPtoHistory(userId: $userId) {
      employeeId
      employeeName
      currentAnnualAllowance
      currentYearRemaining
      years {
        year
        daysUsed
        daysScheduled
        requests {
          id
          startDate
          endDate
          approvalStatus
          reason
          createdAt
          reviewedAt
          dates {
            date
            balanceImpact
          }
        }
      }
    }
  }
`;

export const GET_DASHBOARD_TEAM_STATUS = gql`
  query GetDashboardTeamStatus($date: Timestamp!, $area: LocationPreference) {
    dashboardTeamStatus(date: $date, area: $area) {
      employeeId
      employeeName
      employeeRole
      employeeAreas
      status
      startDate
      endDate
      daysRemaining
    }
  }
`;
